import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IconStatusChange } from '@tabler/icons-react';
import { CrudMode, CrudSchemaFields, FormItemValueType, type EnsoFormItem } from '@ohpen/enso-ui';

import { ComplianceStatus } from '@pmp/api';
import { translatedValueEnum } from '@pmp/utils';
import { getComplianceStatusTags, renderReadonlyStatusTag } from '@pmp/components';
import { TabProps } from '../interfaces';
import { CrudFormCard } from '../../firms/components/CrudFormCard';

export const StatusTab: React.FC<TabProps> = ({ form, mode, selectsOptions }) => {
  const { t } = useTranslation();
  const readonly = mode === CrudMode.VIEW;

  const columns: EnsoFormItem[] = useMemo(
    () => [
      {
        title: t('lender.brokers.labels.compliance'),
        dataIndex: ['compliance', 'complianceStatus'],
        valueType: FormItemValueType.radio,
        readonly,
        renderReadonly: renderReadonlyStatusTag(getComplianceStatusTags(t)),
        fieldProps: {
          options: [
            { label: t('firms.labels.pending'), value: ComplianceStatus.PENDING },
            { label: t('firms.labels.failed'), value: ComplianceStatus.FAILED },
            { label: t('firms.labels.passed'), value: ComplianceStatus.PASSED },
            { label: t('firms.labels.reReviewRequired'), value: ComplianceStatus.RE_REVIEW_REQUIRED },
          ],
        },
        colProps: { span: 24 },
      },
      {
        title: t('lender.brokers.labels.watchlist'),
        dataIndex: ['compliance', 'onTheWatchlist'],
        valueType: 'radio',
        readonly,
        fieldProps: {
          options: [
            { label: t('enso.labels.yes'), value: true },
            { label: t('enso.labels.no'), value: false },
          ],
        },
        formItemProps: {
          rules: [{ required: true }],
        },
        colProps: { span: 24 },
      },
      ...(mode === CrudMode.CREATE
        ? []
        : ([
            {
              title: t('lender.brokers.labels.complianceRecord'),
              valueType: 'group',
              colProps: { span: 24 },
              rowProps: { gutter: [24, 0] },
              columns: [
                {
                  title: t('firms.labels.addedToPanel'),
                  dataIndex: 'addedToPanel',
                  valueType: 'date',
                  readonly: true,
                  colProps: { span: 6 },
                },
                {
                  title: t('global.labels.addedBy'),
                  dataIndex: 'addedBy',
                  valueType: 'text',
                  valueEnum: translatedValueEnum(t, 'global.systemActors'),
                  readonly: true,
                  colProps: { span: 6 },
                },
                {
                  title: t('global.labels.lastUpdate'),
                  dataIndex: 'lastUpdate',
                  valueType: 'date',
                  readonly: true,
                  colProps: { span: 6 },
                },
                {
                  title: t('global.labels.updatedBy'),
                  dataIndex: 'updatedBy',
                  valueType: 'text',
                  valueEnum: translatedValueEnum(t, 'global.systemActors'),
                  readonly: true,
                  colProps: { span: 6 },
                },
              ],
            },
            {
              valueType: 'group',
              colProps: { span: 24 },
              rowProps: { gutter: [24, 0] },
              columns: [
                {
                  title: t('lender.brokers.labels.activationDate'),
                  dataIndex: 'activationDate',
                  valueType: 'date',
                  readonly: true,
                  colProps: { span: 6 },
                },
                {
                  title: t('lender.brokers.labels.deactivationDate'),
                  dataIndex: 'deactivationDate',
                  valueType: 'date',
                  readonly: true,
                  colProps: { span: 6 },
                },
              ],
            },
          ] satisfies EnsoFormItem[])),
    ],
    [readonly, t],
  );

  return (
    <CrudFormCard collapsible={readonly} icon={<IconStatusChange />} title={t('firms.labels.status')}>
      <CrudSchemaFields schema={columns} form={form} readonly={readonly} component={false} />
    </CrudFormCard>
  );
};
